import { CaptionsToken, CaptionsTokenType } from 'data/CaptionsToken';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Overlay } from '../components/Overlay';
import { results } from './podcast-test-1';
import WaveformView from '../audio/WaveformView';
import { ButtonIcon } from 'controller/components/ButtonIcon';
import {
  canHover,
  compressAudioFile,
  generateClassName,
  getXFromPointerEvent,
  hasParentWithClassName,
  isEditingText,
  newUUID,
  selectLocalFiles
} from 'utils';
import { getStoreState, storeDispatch } from 'state/Store';
import * as Actions from 'state/actions';
import {
  cancelTranscribeJob,
  SUPPORTED_LANGUAGES,
  transcribe
} from 'utils/Transcribe';
import {
  deleteFileWithUrl,
  getAvailableFilenameWithExtension,
  uploadFileCancellable
} from 'utils/S3Utils';
import { bucketInternalBaseUrl } from 'utils/AWSConfig';
import { reportBFError, trackEvent } from 'utils/Tracker';
import { ProgressBar } from 'components/ProgressBar';
import FileType from 'data/FileType';
import srtParser2 from 'srt-parser-2';
const toWav = require('audiobuffer-to-wav');

const tokenizeItems = (items: any[]): CaptionsToken[] => {
  const tokens: CaptionsToken[] = [];
  items.forEach((item: any) => {
    if (item.type === 'punctuation' && tokens.length) {
      const lastToken = tokens[tokens.length - 1];
      lastToken.value += item.alternatives[0].content;
      return;
    }
    const t: CaptionsToken = {
      key: newUUID(),
      type: CaptionsTokenType.Text,
      start: parseFloat(item.start_time!),
      end: parseFloat(item.end_time!),
      value: item.alternatives[0].content
    };
    tokens[tokens.length] = t;
  });
  return tokens;
};

const tokenizeSRTFile = (fileContent: string): CaptionsToken[] => {
  const parser = new srtParser2();
  const pages = parser.fromSrt(fileContent);
  const tokens: CaptionsToken[] = pages.map(p => {
    return {
      key: p.id,
      type: CaptionsTokenType.Text,
      start: p.startSeconds,
      end: p.endSeconds,
      value: p.text
    };
  });
  return tokens;
};

enum DragType {
  LeftEdge,
  Whole,
  RightEdge
}

const ZOOM_LEVELS = [0.25, 0.5, 1, 2, 4];

const getLength = (seconds: number, zoom: number): number => {
  return seconds * 160 * zoom;
};

const getDuration = (pixels: number, zoom: number): number => {
  return pixels / 160 / zoom;
};

const formatSeconds = (seconds: number): string => {
  seconds = Math.round(seconds);
  if (seconds < 10) {
    return `0:0${seconds}`;
  } else if (seconds < 60) {
    return `0:${seconds}`;
  } else {
    const minutes = Math.floor(seconds / 60);
    seconds %= 60;
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  }
};

interface TokenProps {
  data: CaptionsToken;
  selected: boolean;
  editing: boolean;
  dragging: boolean;
  zoom: number;
  onSelected?: (data: CaptionsToken) => void;
  onEdit?: (data: CaptionsToken) => void;
  onEditEnded: () => void;
}

let lastClick = 0;

class TokenView extends React.Component<TokenProps> {
  inputEl = React.createRef<HTMLInputElement>();
  prevValue = '';

  componentDidMount() {
    this.inputEl.current?.focus();
    this.resizeInput();
  }

  didClick = () => {
    const { selected, onSelected, data } = this.props;
    if (!selected && onSelected) {
      onSelected(data);
    } else if (!canHover() && Date.now() - lastClick < 250) {
      this.didDoubleClick();
    }
    lastClick = Date.now();
  };

  didDoubleClick = () => {
    const { onEdit, data } = this.props;
    onEdit?.(data);
  };

  componentDidUpdate(prevProps: TokenProps) {
    if (!prevProps.editing && this.props.editing) {
      // focus
      this.prevValue = this.props.data.value;
      this.inputEl.current?.focus();
      this.resizeInput();
      document.querySelector('.captions')?.scrollTo({ left: 0 }); // hack to prevent auto scroll on mobile
    }
  }

  cancelEditing() {
    this.props.data.value = this.prevValue;
    this.props.onEditEnded();
  }

  confirmEditing() {
    if (this.prevValue === '') {
      // assign auto duration
      const input = this.inputEl.current;
      if (input) {
        const { width } = input.getBoundingClientRect();
        const { data, zoom } = this.props;
        const duration = getDuration(width, zoom);
        data.end = data.start + duration;
      }
    }
    this.props.onEditEnded();
  }

  onContentChange = () => {
    this.props.data.value = this.inputEl.current!.value;
    this.forceUpdate(this.resizeInput);
  };

  resizeInput = () => {
    const input = this.inputEl.current;
    if (input) {
      const {
        data: { start, end },
        zoom
      } = this.props;
      input.style.width = `max(${Math.max(1, input.value.length) + 'ch'}, ${
        getLength(end - start, zoom) - 10
      }px)`;
    }
  };

  preventKeys = (e: React.KeyboardEvent) => {
    if (e.which === 9) {
      // tab
      e.preventDefault();
    }
  };

  render() {
    const { data, zoom, selected, editing, dragging } = this.props;
    const isOnlySelected = selected && !editing;
    return (
      <div
        className={generateClassName('token', {
          selected: isOnlySelected,
          editing,
          dragging
        })}
        style={{
          left: getLength(data.start, zoom),
          width: editing ? 'inherit' : getLength(data.end - data.start, zoom)
        }}
        onClick={this.didClick}
        onDoubleClick={this.didDoubleClick}
      >
        {editing ? (
          <input
            ref={this.inputEl}
            type="text"
            value={this.props.data.value}
            onKeyDown={this.preventKeys}
            onChange={this.onContentChange}
          />
        ) : (
          <span>{this.props.data.value}</span>
        )}
        {isOnlySelected && !dragging && (
          <>
            <div className="edge edge-left" />
            <div className="edge edge-right" />
          </>
        )}
      </div>
    );
  }
}

interface Props {
  audioBuffer: AudioBuffer;
  initialLanguageCode?: string;
  initialTokens?: CaptionsToken[];
}

enum ViewState {
  PickLanguage = 'Pick language',
  CompressingAudio = 'Compressing audio file',
  Uploading = 'Uploading',
  Processing = 'Processing',
  Default = 'Default',
  Selected = 'Selected',
  Editing = 'Editing',
  Error = 'Error'
}

interface State {
  zoomLevel: number;
  state: ViewState;
  tokens?: CaptionsToken[];
  isPlaying: boolean;
  selectedToken: CaptionsToken | null;
  isDragging: boolean;
  selectedLanguage: string;
  progress: number | undefined;
}

const MIN_DRAG_MOVEMENT = 15;

export class CaptionsModal extends React.Component<Props, State> {
  audio = new Audio();
  audioFile: File;
  updateRequest = 0;
  timeline = React.createRef<HTMLDivElement>();
  selectedTokenViewRef = React.createRef<TokenView>();
  initialDragPosition = 0;
  initialDragStart = 0;
  dragType = DragType.Whole;
  wasPlaying = false;
  isModalMounted = false;
  previousOnbeforeunloadHandler: (e: BeforeUnloadEvent) => void | null;

  constructor(props: Props) {
    super(props);

    const wav = toWav(props.audioBuffer);
    this.audioFile = new File([wav], 'audio.wav', { type: 'audio/wav' });
    const src = URL.createObjectURL(this.audioFile);
    this.audio.autoplay = false;
    this.audio.src = src;

    let tokens: CaptionsToken[] | undefined;
    if (props.initialTokens) {
      tokens = JSON.parse(JSON.stringify(props.initialTokens));
    }

    const state = tokens ? ViewState.Default : ViewState.PickLanguage;
    this.state = {
      zoomLevel: 1,
      state,
      tokens,
      isPlaying: false,
      selectedToken: null,
      isDragging: false,
      selectedLanguage: props.initialLanguageCode || 'none',
      progress: 0
    };
    this.audio.onended = () => {
      cancelAnimationFrame(this.updateRequest);
      this.setState({ isPlaying: false });
    };
  }

  async compressAudio() {
    this.setState({
      state: ViewState.CompressingAudio,
      progress: undefined
    });
    const file = await compressAudioFile(this.audioFile, true);
    if (this.isModalMounted) {
      this.uploadAudio(file);
    }
  }

  async uploadAudio(compressedFile: File) {
    this.setState({
      state: ViewState.Uploading,
      progress: 0
    });
    const fileId = await getAvailableFilenameWithExtension('mp3');
    const filename = `${fileId}.mp3`;
    console.log(`Will upload file: ${filename}`);
    uploadFileCancellable(
      filename,
      compressedFile,
      'audio/mp3',
      () => {
        console.log('Uploaded audio');
        const audioS3Url = bucketInternalBaseUrl + filename;
        if (this.isModalMounted) {
          this.transcribe(audioS3Url);
        } else {
          deleteFileWithUrl(audioS3Url);
        }
      },
      (error: string) => {
        //   reportBFError(new Error(error));
        alert(error);
      },
      (progress: number) => {
        this.setState({
          progress
        });
      }
    );
  }

  async transcribe(audioS3Url: string) {
    this.setState({
      state: ViewState.Processing,
      progress: undefined
    });
    try {
      trackEvent('Transcribe', { lang: this.state.selectedLanguage });
      const data = await transcribe(audioS3Url, this.state.selectedLanguage);
      console.log(data);
      if (this.isModalMounted) {
        const items = data.results.items;
        this.setState({
          state: ViewState.Default,
          tokens: tokenizeItems(items)
        });
      }
    } catch (err) {
      if (this.isModalMounted) {
        reportBFError(err);
        this.setState({
          state: ViewState.Error
        });
      } else {
        console.log('Ingore failure, the modal was dismissed.');
      }
    }
    deleteFileWithUrl(audioS3Url);
  }

  componentDidMount() {
    this.previousOnbeforeunloadHandler = (window as any).onbeforeunload;
    window.onbeforeunload = (e: BeforeUnloadEvent) => {
      if (
        this.state.tokens &&
        JSON.stringify(this.state.tokens) !==
          JSON.stringify(getStoreState().flyer.captions)
      ) {
        e.returnValue =
          'You have unsaved changes, are you sure you want to leave?';
      } else {
        if (this.previousOnbeforeunloadHandler) {
          this.previousOnbeforeunloadHandler(e);
        }
      }
    };
    document.addEventListener('keyup', this.onKeyUp);
    this.isModalMounted = true;
  }

  componentWillUnmount() {
    window.onbeforeunload = this.previousOnbeforeunloadHandler;
    this.isModalMounted = false;
    document.removeEventListener('keyup', this.onKeyUp);
    cancelAnimationFrame(this.updateRequest);
    this.audio.pause();
    const src = this.audio.src;
    this.audio.srcObject = null;
    URL.revokeObjectURL(src);
    if (this.state.state === ViewState.Processing) {
      cancelTranscribeJob();
    }
  }

  selectPreviousToken() {
    const { tokens, selectedToken } = this.state;
    if (!tokens || !selectedToken) {
      return;
    }
    const index = tokens.indexOf(selectedToken);
    if (index > 0) {
      this.setState({
        selectedToken: tokens[index - 1]
      });
    }
  }

  selectNextToken() {
    const { tokens, selectedToken } = this.state;
    if (!tokens || !selectedToken) {
      return;
    }
    const index = tokens.indexOf(selectedToken);
    if (index < tokens.length - 1) {
      this.setState({
        selectedToken: tokens[index + 1]
      });
    }
  }

  onKeyUp = (event: KeyboardEvent) => {
    const { state } = this.state;
    if (event.code === 'Escape') {
      if (state === ViewState.Editing) {
        this.confirmEditing();
      } else if (state === ViewState.Selected) {
        this.setState({
          state: ViewState.Default,
          selectedToken: null
        });
      }
      return;
    }
    if (isEditingText()) {
      if (state === ViewState.Editing) {
        switch (event.code) {
          case 'Enter':
            this.confirmEditing();
            break;
          case 'Tab':
            this.confirmEditing();
            setTimeout(() => {
              if (event.shiftKey) {
                this.selectPreviousToken();
              } else {
                this.selectNextToken();
              }
            }, 1);
            break;
          default: // do nothing
        }
      }
      return;
    }
    if (state === ViewState.Selected) {
      switch (event.code) {
        case 'Backspace':
        case 'Delete':
          this.deleteSelectedToken();
          return;
        case 'Enter':
          this.setState({
            state: ViewState.Editing
          });
          return;
        case 'Tab':
          if (event.shiftKey) {
            this.selectPreviousToken();
          } else {
            this.selectNextToken();
          }
          return;
        case 'ArrowRight':
          this.selectNextToken();
          return;
        case 'ArrowLeft':
          this.selectPreviousToken();
          return;
        default: // do nothing
      }
    }
    if (event.code === 'Space') {
      this.togglePlayback();
    }
  };

  didPressOnWindow = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    const target = e.target as HTMLElement;
    if (this.state.state === ViewState.Selected) {
      if (this.selectedTokenViewRef.current) {
        const tokenView = ReactDOM.findDOMNode(
          this.selectedTokenViewRef.current
        ) as HTMLElement;
        if (target === tokenView || tokenView.contains(target)) {
          this.dragType = DragType.Whole;
          if (target.classList.contains('edge-left')) {
            this.dragType = DragType.LeftEdge;
          } else if (target.classList.contains('edge-right')) {
            this.dragType = DragType.RightEdge;
          }
          this.initialDragPosition = getXFromPointerEvent(e.nativeEvent);
          this.initialDragStart = this.state.selectedToken!.start;
          window.addEventListener('mousemove', this.didDragToken);
          window.addEventListener('touchmove', this.didDragToken);
          return;
        }
      }
    }
  };

  didDragToken = (e: MouseEvent | TouchEvent) => {
    let { isDragging, zoomLevel } = this.state;
    const clientX = getXFromPointerEvent(e);
    if (!isDragging) {
      const d = Math.abs(clientX - this.initialDragPosition);
      if (d > MIN_DRAG_MOVEMENT) {
        isDragging = true;
        this.setState({ isDragging });
      }
    }
    if (isDragging) {
      const token = this.state.selectedToken!;
      if (this.dragType === DragType.Whole) {
        const offset = getDuration(
          clientX - this.initialDragPosition,
          zoomLevel
        );
        // console.log('OFFSET: ' + offset);
        const tokenDuration = token.end - token.start;
        const newStart = Math.max(
          0,
          Math.min(
            this.props.audioBuffer.duration - tokenDuration,
            this.initialDragStart + offset
          )
        );
        token.start = newStart;
        token.end = newStart + tokenDuration;
      } else {
        const mouseX = clientX - window.innerWidth / 2;
        const time = getDuration(mouseX, zoomLevel);
        const newTime = Math.max(
          0,
          Math.min(
            this.props.audioBuffer.duration,
            this.audio.currentTime + time
          )
        );
        if (this.dragType === DragType.LeftEdge) {
          token.start = newTime;
        } else {
          token.end = newTime;
        }
      }
      this.forceUpdate();
    }
  };

  didClickOnWindow = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    window.removeEventListener('mousemove', this.didDragToken);
    window.removeEventListener('touchmove', this.didDragToken);
    if (this.state.isDragging) {
      // console.log('STOP DRAGGING');
      // TODO: change token position
      const tokens = [...this.state.tokens!];
      tokens.sort((a, b) => {
        return a.start - b.start;
      });
      this.setState({ isDragging: false, tokens });
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const target = e.target as HTMLElement;
    if (
      hasParentWithClassName(target, 'captions-controls', true) ||
      hasParentWithClassName(target, 'captions-playback', true)
    ) {
      // console.log(`IGNORE - It's a control button`);
      return;
    }
    if (this.selectedTokenViewRef.current) {
      const tokenView = ReactDOM.findDOMNode(
        this.selectedTokenViewRef.current
      ) as HTMLElement;
      if (target === tokenView || tokenView.contains(target)) {
        // console.log(`IGNORE - It's in selected token`);
        return;
      }
    }
    if (hasParentWithClassName(target, 'token')) {
      // console.log(`IGNORE - It's in another token`);
      return;
    }
    if (this.state.state === ViewState.Editing) {
      this.confirmEditing();
    } else if (this.state.state === ViewState.Selected) {
      this.setState({
        state: ViewState.Default,
        selectedToken: null
      });
    }
  };

  didPressWave = (
    e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>
  ) => {
    this.wasPlaying = false;
    if (this.state.isPlaying) {
      this.wasPlaying = true;
      this.audio.pause();
      cancelAnimationFrame(this.updateRequest);
      this.setState({ isPlaying: false });
    }
    this.initialDragPosition = getXFromPointerEvent(e.nativeEvent);
    this.initialDragStart = this.audio.currentTime;
    window.addEventListener('mousemove', this.didDragTimeline);
    window.addEventListener('mouseup', this.didStopDraggingTimeline);

    window.addEventListener('touchmove', this.didDragTimeline);
    window.addEventListener('touchend', this.didStopDraggingTimeline);
  };

  didDragTimeline = (e: MouseEvent | TouchEvent) => {
    const { zoomLevel } = this.state;
    const offset = getDuration(
      getXFromPointerEvent(e) - this.initialDragPosition,
      zoomLevel
    );
    this.audio.currentTime = Math.max(
      0,
      Math.min(this.props.audioBuffer.duration, this.initialDragStart - offset)
    );
    this.updateTimeline();
  };

  didStopDraggingTimeline = () => {
    window.removeEventListener('mousemove', this.didDragTimeline);
    window.removeEventListener('mouseup', this.didStopDraggingTimeline);

    window.removeEventListener('touchmove', this.didDragTimeline);
    window.removeEventListener('touchend', this.didStopDraggingTimeline);
    if (this.wasPlaying) {
      this.audio.play();
      this.update();
      this.setState({
        isPlaying: true
      });
    }
  };

  togglePlayback = () => {
    const isPlaying = !this.state.isPlaying;
    if (isPlaying) {
      if (this.state.selectedToken) {
        this.audio.currentTime = this.state.selectedToken.start;
      }
      this.audio.play();
      this.update();
    } else {
      this.audio.pause();
      cancelAnimationFrame(this.updateRequest);
    }
    this.setState({
      isPlaying
    });
  };

  changeZoom = (zoomLevel: number) => {
    this.setState(
      {
        zoomLevel
      },
      () => {
        if (!this.state.isPlaying) {
          this.updateTimeline();
        }
      }
    );
  };

  zoomIn = () => {
    const currentIndex = ZOOM_LEVELS.indexOf(this.state.zoomLevel);
    this.changeZoom(ZOOM_LEVELS[currentIndex + 1]);
  };

  zoomOut = () => {
    const currentIndex = ZOOM_LEVELS.indexOf(this.state.zoomLevel);
    this.changeZoom(ZOOM_LEVELS[currentIndex - 1]);
  };

  update = () => {
    this.updateTimeline();
    this.updateRequest = requestAnimationFrame(this.update);
  };

  updateTimeline = () => {
    const offset = getLength(this.audio.currentTime, this.state.zoomLevel);
    if (this.timeline.current) {
      this.timeline.current.style.transform = `translateX(-${offset}px)`;
    }
  };

  didSelect = (selectedToken: CaptionsToken) => {
    this.setState({
      selectedToken,
      state: ViewState.Selected
    });
  };

  startEditing = (selectedToken: CaptionsToken) => {
    this.setState({
      selectedToken,
      state: ViewState.Editing
    });
  };

  didEndEditing = () => {
    let { selectedToken } = this.state;
    if (selectedToken && selectedToken.value.trim().length === 0) {
      const tokens = [...this.state.tokens!];
      // remove token
      const index = tokens.indexOf(selectedToken);
      if (index > -1) {
        tokens.splice(index, 1);
      }
      selectedToken = null;
      this.setState({
        state: ViewState.Default,
        tokens,
        selectedToken: null
      });
      return;
    }
    this.setState({
      state: ViewState.Selected
    });
  };

  addToken = () => {
    const token: CaptionsToken = {
      key: newUUID(),
      type: CaptionsTokenType.Text,
      start: this.audio.currentTime,
      end: this.audio.currentTime,
      value: ''
    };
    let index = 0;
    const tokens = [...this.state.tokens!];
    while (tokens[index].start < token.start) {
      index++;
    }
    tokens.splice(index, 0, token);
    this.setState({
      selectedToken: token,
      tokens,
      state: ViewState.Editing
    });
  };

  editSelectedToken = () => {
    this.setState({ state: ViewState.Editing });
  };

  deleteSelectedToken = () => {
    const tokens = [...this.state.tokens!];
    const index = tokens.indexOf(this.state.selectedToken!);
    tokens.splice(index, 1);
    this.setState({
      selectedToken: null,
      tokens,
      state: ViewState.Default
    });
  };

  confirmEditing = () => {
    this.selectedTokenViewRef.current?.confirmEditing();
  };

  cancelEditing = () => {
    this.selectedTokenViewRef.current?.cancelEditing();
  };

  renderButtons = (): JSX.Element | null => {
    const { state } = this.state;
    switch (state) {
      case ViewState.Default:
        return <ButtonIcon icon="plus" onClick={this.addToken} />;
      case ViewState.Selected:
      case ViewState.Editing:
        return (
          <>
            <ButtonIcon
              icon="edit"
              onClick={this.editSelectedToken}
              disabled={state === ViewState.Editing}
            />
            <ButtonIcon
              icon="delete-color"
              onClick={this.deleteSelectedToken}
            />
          </>
        );
      default:
        return null;
    }
  };

  changeLanguage = () => {
    this.setState({
      state: ViewState.PickLanguage
    });
  };

  save = () => {
    storeDispatch(
      Actions.saveCaptions(this.state.tokens!, this.state.selectedLanguage)
    );
  };

  onLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const languageCode = e.target.value;
    this.setState({
      selectedLanguage: languageCode
    });
    storeDispatch(Actions.changedTranscribeSpokenLanguage(languageCode));
  };

  startTranscription = () => {
    this.compressAudio();
  };

  mockTranscription = () => {
    const tokens = tokenizeItems(results.results.items);
    this.setState({
      state: ViewState.Default,
      tokens
    });
  };

  selectSRTFile = async () => {
    const files = await selectLocalFiles(FileType.SRT);
    if (files.length) {
      const srtFile = files[0];
      const fr = new FileReader();
      fr.onload = () => {
        const tokens = tokenizeSRTFile(fr.result as string);
        this.setState({
          state: ViewState.Default,
          tokens
        });
      };
      fr.readAsText(srtFile);
    }
  };

  render() {
    const {
      state,
      zoomLevel,
      tokens,
      isPlaying,
      selectedToken,
      selectedLanguage,
      progress
    } = this.state;
    if (state === ViewState.PickLanguage) {
      return (
        <Overlay opaque={true}>
          <h1>Transcribe soundtrack</h1>
          <div className="captions-language-selection">
            <div className="bf-select">
              <select value={selectedLanguage} onChange={this.onLanguageChange}>
                <option value="none" disabled={true} hidden={true}>
                  Select language
                </option>
                {Object.keys(SUPPORTED_LANGUAGES).map((k: string) => (
                  <option value={k} key={k}>
                    {SUPPORTED_LANGUAGES[k]}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="large primary"
              disabled={selectedLanguage === 'none'}
              onClick={this.startTranscription}
            >
              Transcribe
            </button>
          </div>
          <h2 style={{ marginTop: 40, marginBottom: 15 }}>
            or import{' '}
            <a
              href="https://www.speechpad.com/captions/srt"
              target="_blank"
              rel="noopener noreferrer"
            >
              SRT
            </a>{' '}
            file
          </h2>
          <button className="medium secondary" onClick={this.selectSRTFile}>
            Select file
          </button>
        </Overlay>
      );
    }
    if (state === ViewState.Error) {
      return (
        <Overlay opaque={true}>
          <h1>Something went wrong. Please try again.</h1>
        </Overlay>
      );
    }
    if (
      [
        ViewState.Processing,
        ViewState.Uploading,
        ViewState.CompressingAudio
      ].includes(state)
    ) {
      let text: string = state;
      if (state === ViewState.Processing) {
        text += ', might take a minute';
      }
      return (
        <Overlay opaque={true}>
          <h1>
            {text}
            <span className="ellipsis-anim">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </h1>
          <ProgressBar level={progress} />
        </Overlay>
      );
    }
    const { audioBuffer } = this.props;
    const timelineWidth = getLength(audioBuffer.duration, zoomLevel);
    const seconds: number[] = [];
    let sec = 0;
    while (sec < audioBuffer.duration) {
      seconds.push(sec);
      sec++;
    }
    const hover = canHover();
    return (
      <Overlay fullWidth={true} opaque={true}>
        <div
          className="captions"
          onMouseDown={hover ? this.didPressOnWindow : undefined}
          onMouseUp={hover ? this.didClickOnWindow : undefined}
          onTouchStart={hover ? undefined : this.didPressOnWindow}
          onTouchEnd={hover ? undefined : this.didClickOnWindow}
        >
          <h1>Captions for {SUPPORTED_LANGUAGES[selectedLanguage]}</h1>
          <div className="captions-timeline">
            <div className="timeline" ref={this.timeline}>
              <WaveformView
                width={timelineWidth}
                height={160}
                buffer={audioBuffer}
                onMouseDown={hover ? this.didPressWave : undefined}
                onTouchStart={hover ? undefined : this.didPressWave}
              />
              {seconds.map(s => {
                return (
                  <div
                    className="second"
                    key={s}
                    style={{
                      transform: `translateX(${getLength(s, zoomLevel)}px)`
                    }}
                  >
                    <span>{formatSeconds(s)}</span>
                  </div>
                );
              })}
              {tokens &&
                tokens.map((t, index) => {
                  return (
                    <TokenView
                      data={t}
                      key={t.key}
                      selected={t === selectedToken}
                      ref={
                        t === selectedToken ? this.selectedTokenViewRef : null
                      }
                      editing={
                        t === selectedToken && state === ViewState.Editing
                      }
                      dragging={
                        t === selectedToken &&
                        this.state.isDragging &&
                        this.dragType === DragType.Whole
                      }
                      zoom={zoomLevel}
                      onSelected={
                        !this.state.isDragging ? this.didSelect : undefined
                      }
                      onEdit={
                        !this.state.isDragging ? this.startEditing : undefined
                      }
                      onEditEnded={this.didEndEditing}
                    />
                  );
                })}
            </div>
          </div>
          <div className="captions-controls">
            {this.renderButtons()}
            <div className="zoom">
              <ButtonIcon
                icon="zoom-in"
                onClick={this.zoomIn}
                disabled={
                  ZOOM_LEVELS.indexOf(zoomLevel) >= ZOOM_LEVELS.length - 1
                }
              />
              <ButtonIcon
                icon="zoom-out"
                onClick={this.zoomOut}
                disabled={ZOOM_LEVELS.indexOf(zoomLevel) < 1}
              />
            </div>
          </div>
          <div className="captions-playback">
            <ButtonIcon
              icon={isPlaying ? 'pause' : 'play'}
              onClick={this.togglePlayback}
            />
          </div>
          <div className="captions-buttons">
            <button className="large secondary" onClick={this.changeLanguage}>
              Change language
            </button>
            <button
              className="large primary save-btn"
              disabled={tokens === undefined}
              onClick={this.save}
            >
              Save
            </button>
          </div>
          <div className="needle" />
        </div>
      </Overlay>
    );
  }
}



// WEBPACK FOOTER //
// ./src/captions/CaptionsModal.tsx